import { Injectable } from '@angular/core';
import { AppConfig, UvExplorerClient } from 'common';
import { Observable, Subject } from 'rxjs/Rx';
import { map } from 'rxjs/operators';
import { ScheduledDiscoveryResultDto, RollupDto, SearchDeviceListDto } from 'UvExplorerServer.Dtos.TS/Discovery';
import { ViewerDeviceGroupDto } from 'UvExplorerServer.Dtos.TS/Viewer';
import { GetSchedDiscResultsResultDto } from 'UvExplorerServer.Dtos.TS/Data';
import {
	DisplayDeviceGroupTreeDto, DisplayDeviceGroupContainerDto, DisplayDeviceGroupDto,
	DisplayDeviceListDto, DeviceGroupUpdateResultDto, GetDeviceGroupMembersResultDto,
	DeviceDto, InfoSetView, GetDeviceResultDto, ISetPortStateResultDto, IGetPortStateResultDto,
	PortStateDto
} from 'UvExplorerServer.Dtos.TS/Session';
import { PrtgSettingsSummaryDto } from 'UvExplorerServer.Dtos.TS/Session/PrtgSettingsSummaryDto';
import { PrtgGroupNodeDto } from 'UvExplorerServer.Dtos.TS/Session/PrtgGroupNodeDto';
import { PrtgMapDto } from 'UvExplorerServer.Dtos.TS/Session/PrtgMapDto';
import { ValueEncoder, List } from 'DiscoveryEngine.TS/Utils';
import { DeviceFilter } from 'DiscoveryEngine.TS/Core';
import { ObjSerUtils } from 'DiscoveryEngine.TS/Serialization';
import { LayoutSettings } from 'DiscoveryEngine.TS/Topology';
import { DiffResult } from 'DiscoveryEngine.TS/CommandLine';
import { TopoMap } from 'topomap';
import { AddressType } from './discresults.service';
import { PortStateType } from 'UvExplorerServer.Dtos.TS/Session';


// Field-by-field mirror of ExportToPrtgRequestDto's non-routing options.
// PRTG terminology uses "sensor" rather than "monitor"; the server maps these
// onto the equivalent AddXxxMonitors properties of ExportToPrtgOptions.
export interface ExportToPrtgOptions {
	parentGroupId: number;

	createNewGroup: boolean;
	createDependency: boolean;
	removeMissingDevices: boolean;
	exportPrtgDevicesOnly: boolean;
	exportMapOnly: boolean;
	mergeMapOnExport: boolean;
	exportMasterNetworkOnly: boolean;
	useTemplateTags: boolean;

	createTopologyMap: boolean;
	overwriteExistingTopologyMap: boolean;

	addPingSensors: boolean;
	addSnmpUpTimeSensors: boolean;
	addSnmpCpuSensors: boolean;
	addSnmpMemorySensors: boolean;
	addSnmpDiskFreeSensors: boolean;
	addSnmpTrafficSensors: boolean;
	useSnmpIfName: boolean;
	configAllInterfacesIfUp: boolean;
	configAllPhysicalInterfaces: boolean;
	configAllUpLinkInterfaces: boolean;
	snmpIfSensorFormat: string;
	configAllApSensors: boolean;

	addWmiUpTimeSensors: boolean;
	addWmiCpuSensors: boolean;
	addWmiDiskSpaceSensors: boolean;
	addWmiMemorySensors: boolean;
}

// Mirror of ExportToPrtgMapRequestDto's non-routing options.
export interface ExportToPrtgMapOptions {
	overwriteExistingTopologyMap: boolean;
	showIfLabels: boolean;
	defaultIconHeight: number;
	defaultIconWidth: number;
	addPingSensors: boolean;
}


@Injectable()
export class DiscResultsWebService {

	constructor(private appConfig: AppConfig, private uvExplorerClient: UvExplorerClient) {
		return;
	}

	public getSchedDiscResults(schedDiscGuid: string, dataSelector: string): Observable<GetSchedDiscResultsResultDto> {
		let url = this.appConfig.dataServiceUrl + `api/data/scheddiscresults/scheddisc/guid/${schedDiscGuid}/${dataSelector}`;
		return this.uvExplorerClient.getObjSer<GetSchedDiscResultsResultDto>(url, GetSchedDiscResultsResultDto);
	}

	public getLatestSchedDiscResult(schedDiscGuid: string, dataSelector: string): Observable<ScheduledDiscoveryResultDto> {
		let url = this.appConfig.dataServiceUrl + `api/data/scheddiscresults/scheddisc/guid/${schedDiscGuid}/latest/${dataSelector}`;
		return this.uvExplorerClient.getObjSer<ScheduledDiscoveryResultDto>(url, ScheduledDiscoveryResultDto);
	}

	public getSchedDiscResultByGuid(schedDiscRsltGuid: string, dataSelector: string): Observable<ScheduledDiscoveryResultDto> {
		let url = this.appConfig.dataServiceUrl + `api/data/scheddiscresults/guid/${schedDiscRsltGuid}/${dataSelector}`;
		return this.uvExplorerClient.getObjSer<ScheduledDiscoveryResultDto>(url, ScheduledDiscoveryResultDto);
	}

	public deleteSchedDiscResultByGuid(schedDiscRsltGuid: string): Observable<boolean> {
		let url = this.appConfig.dataServiceUrl + `api/data/scheddiscresults/guid/${schedDiscRsltGuid}`;
		return this.uvExplorerClient.deleteNoResult(url, "");
	}

	public deleteResultsForSchedDisc(schedDiscGuid: string): Observable<boolean> {
		let url = this.appConfig.dataServiceUrl + `api/data/scheddiscresults/scheddiscguid/${schedDiscGuid}`;
		return this.uvExplorerClient.deleteNoResult(url, "");
	}

	public getAgentRollup(agentGuid: string, dataSelector: string): Observable<RollupDto> {
		let url = this.appConfig.dataServiceUrl + `api/data/rollups/agent/guid/${agentGuid}/${dataSelector}`;
		return this.uvExplorerClient.getObjSer<RollupDto>(url, RollupDto);
	}

	public getNetViewRollup(netViewGuid: string, dataSelector: string): Observable<RollupDto> {
		let url = this.appConfig.dataServiceUrl + `api/data/rollups/netview/guid/${netViewGuid}/${dataSelector}`;
		return this.uvExplorerClient.getObjSer<RollupDto>(url, RollupDto);
	}

	public loadNetwork(sessionGuid: string, netViewGuid: string, agentGuid: string,
		schedDiscGuid: string, schedDiscRsltGuid: string): Observable<boolean> {

		let body = {
			load_network_request: {
				session_guid: sessionGuid,
				net_view_guid: netViewGuid,
				agent_guid: agentGuid,
				sched_disc_guid: schedDiscGuid,
				sched_disc_result_guid: schedDiscRsltGuid,
			}
		};

		let url = this.appConfig.sessionServiceUrl + `api/session/loadnetwork`;
		return this.uvExplorerClient.postNoResult(url, body);
	}

	public unloadNetwork(sessionGuid: string): Observable<boolean> {
		let url = this.appConfig.sessionServiceUrl + `api/session/unloadnetwork/${sessionGuid}`;
		return this.uvExplorerClient.deleteNoResult(url, "");
	}

	public getDeviceGroupTree(sessionGuid: string, netViewGuid: string): Observable<DisplayDeviceGroupTreeDto> {

		let body = {
			get_device_group_tree_request: {
				session_guid: sessionGuid,
				net_view_guid: netViewGuid,
			}
		};

		let url = this.appConfig.sessionServiceUrl + `api/session/devicegrouptree`;
		return this.uvExplorerClient.postObjSer<DisplayDeviceGroupTreeDto>(url, body, DisplayDeviceGroupTreeDto);
	}

	public getTopoMap(sessionGuid: string, netViewGuid: string, agentGuid: string,
		schedDiscGuid: string, schedDiscRsltGuid: string, vdgGuid: string): Observable<TopoMap> {

		let body = {
			get_topo_map_request: {
				session_guid: sessionGuid,
				net_view_guid: netViewGuid,
				agent_guid: agentGuid,
				sched_disc_guid: schedDiscGuid,
				sched_disc_result_guid: schedDiscRsltGuid,
				vdg_guid: vdgGuid,
			}
		};

		let url = this.appConfig.sessionServiceUrl + `api/session/topomap`;
		return this.uvExplorerClient.post<TopoMap>(url, body);
	}

	public moveTopoMapNode(sessionGuid: string, netViewGuid: string, agentGuid: string,
		schedDiscGuid: string, schedDiscRsltGuid: string, vdgGuid: string,
		nodeId: number, newX: number, newY: number): Observable<boolean> {

		let body = {
			move_topo_map_node_request: {
				session_guid: sessionGuid,
				net_view_guid: netViewGuid,
				agent_guid: agentGuid,
				sched_disc_guid: schedDiscGuid,
				sched_disc_result_guid: schedDiscRsltGuid,
				vdg_guid: vdgGuid,
				node_id: ValueEncoder.EncodeInteger(nodeId),
				new_x: ValueEncoder.EncodeInteger(newX),
				new_y: ValueEncoder.EncodeInteger(newY),
			}
		};

		let url = this.appConfig.sessionServiceUrl + `api/session/topomap/movenode`;
		return this.uvExplorerClient.postNoResult(url, body);
	}

	public layoutMap(sessionGuid: string, netViewGuid: string, agentGuid: string,
		schedDiscGuid: string, schedDiscRsltGuid: string, vdgGuid: string,
		layoutSettings: LayoutSettings): Observable<boolean> {

		let body = {
			layout_map_request: {
				session_guid: sessionGuid,
				net_view_guid: netViewGuid,
				agent_guid: agentGuid,
				sched_disc_guid: schedDiscGuid,
				sched_disc_result_guid: schedDiscRsltGuid,
				vdg_guid: vdgGuid,
			}
		};
		layoutSettings.Write(body.layout_map_request, {});

		let url = this.appConfig.sessionServiceUrl + `api/session/topomap/layoutmap`;
		return this.uvExplorerClient.postNoResult(url, body);
	}

	public setMapRoot(sessionGuid: string, netViewGuid: string, agentGuid: string,
		schedDiscGuid: string, schedDiscRsltGuid: string, vdgGuid: string,
		operation: string, nodeId: number): Observable<boolean> {

		let body = {
			set_topo_map_root_request: {
				session_guid: sessionGuid,
				net_view_guid: netViewGuid,
				agent_guid: agentGuid,
				sched_disc_guid: schedDiscGuid,
				sched_disc_result_guid: schedDiscRsltGuid,
				vdg_guid: vdgGuid,
				operation: operation,
				node_id: ValueEncoder.EncodeInteger(nodeId),
			}
		};

		let url = this.appConfig.sessionServiceUrl + `api/session/topomap/setroot`;
		return this.uvExplorerClient.postNoResult(url, body);
	}

	public exportTopoMap(sessionGuid: string, netViewGuid: string, agentGuid: string,
		schedDiscGuid: string, schedDiscRsltGuid: string, vdgGuid: string,
		type: string, includeDeviceLabels: boolean, includeDeviceProperties: boolean,
		includeLinkLabels: boolean, includeLinkProperties: boolean,
		sheetsAcross: number, sheetsDown: number, vsdxFormat: boolean): Observable<Blob> {

		let body = {
			export_topo_map_request: {
				session_guid: sessionGuid,
				net_view_guid: netViewGuid,
				agent_guid: agentGuid,
				sched_disc_guid: schedDiscGuid,
				sched_disc_result_guid: schedDiscRsltGuid,
				vdg_guid: vdgGuid,
				type: type,
				include_device_labels: ValueEncoder.EncodeBoolean(includeDeviceLabels),
				include_device_properties: ValueEncoder.EncodeBoolean(includeDeviceProperties),
				include_link_labels: ValueEncoder.EncodeBoolean(includeLinkLabels),
				include_link_properties: ValueEncoder.EncodeBoolean(includeLinkProperties),
				sheets_across: ValueEncoder.EncodeInteger(sheetsAcross),
				sheets_down: ValueEncoder.EncodeInteger(sheetsDown),
				vsdx_format: ValueEncoder.EncodeBoolean(vsdxFormat),
			}
		};

		let url = this.appConfig.sessionServiceUrl + `api/session/topomap/export`;
		return this.uvExplorerClient.postWithBinaryResult(url, body);
	}

	public exportTopoMapToLucid(sessionGuid: string, netViewGuid: string, agentGuid: string,
		schedDiscGuid: string, schedDiscRsltGuid: string, vdgGuid: string,
		type: string, includeDeviceLabels: boolean, includeDeviceProperties: boolean,
		includeLinkLabels: boolean, includeLinkProperties: boolean,
		sheetsAcross: number, sheetsDown: number, vsdxFormat: boolean): Observable<string> {

		let body = {
			export_topo_map_request: {
				session_guid: sessionGuid,
				net_view_guid: netViewGuid,
				agent_guid: agentGuid,
				sched_disc_guid: schedDiscGuid,
				sched_disc_result_guid: schedDiscRsltGuid,
				vdg_guid: vdgGuid,
				type: type,
				include_device_labels: ValueEncoder.EncodeBoolean(includeDeviceLabels),
				include_device_properties: ValueEncoder.EncodeBoolean(includeDeviceProperties),
				include_link_labels: ValueEncoder.EncodeBoolean(includeLinkLabels),
				include_link_properties: ValueEncoder.EncodeBoolean(includeLinkProperties),
				sheets_across: ValueEncoder.EncodeInteger(sheetsAcross),
				sheets_down: ValueEncoder.EncodeInteger(sheetsDown),
				vsdx_format: ValueEncoder.EncodeBoolean(vsdxFormat),
			}
		};

		let url = this.appConfig.sessionServiceUrl + `api/session/topomap/export/lucid`;
		return this.uvExplorerClient.postWithTextResult(url, body);
	}

	public getDeviceList(sessionGuid: string, netViewGuid: string, agentGuid: string,
		schedDiscGuid: string, schedDiscRsltGuid: string, vdgGuid: string): Observable<DisplayDeviceListDto> {

		let body = {
			get_device_list_request: {
				session_guid: sessionGuid,
				net_view_guid: netViewGuid,
				agent_guid: agentGuid,
				sched_disc_guid: schedDiscGuid,
				sched_disc_result_guid: schedDiscRsltGuid,
				vdg_guid: vdgGuid,
			}
		};

		let url = this.appConfig.sessionServiceUrl + `api/session/devicelist`;
		return this.uvExplorerClient.post<DisplayDeviceListDto>(url, body);
	}

	public getFilteredDeviceList(sessionGuid: string, netViewGuid: string, agentGuid: string,
		schedDiscGuid: string, schedDiscRsltGuid: string, source: string, primaryFilter: DeviceFilter,
		connectedFilter: DeviceFilter, requireSnmp: boolean, requireWmi: boolean, requireVim: boolean,
		requireSsh: boolean, requireConfig: boolean): Observable<DisplayDeviceListDto> {

		let body = {
			get_filtered_device_list_request: {
				session_guid: sessionGuid,
				net_view_guid: netViewGuid,
				agent_guid: agentGuid,
				sched_disc_guid: schedDiscGuid,
				sched_disc_result_guid: schedDiscRsltGuid,
				source: source,
				require_snmp: ValueEncoder.EncodeBoolean(requireSnmp),
				require_wmi: ValueEncoder.EncodeBoolean(requireWmi),
				require_vim: ValueEncoder.EncodeBoolean(requireVim),
				require_ssh: ValueEncoder.EncodeBoolean(requireSsh),
				require_config: ValueEncoder.EncodeBoolean(requireConfig),
			}
		};

		if (primaryFilter) {
			let context = {};
			context[DeviceFilter.ObjSerName] = "primary_device_filter";
			primaryFilter.Write(body.get_filtered_device_list_request, context);
		}
		if (connectedFilter) {
			let context = {};
			context[DeviceFilter.ObjSerName] = "connected_device_filter";
			connectedFilter.Write(body.get_filtered_device_list_request, context);
		}

		let url = this.appConfig.sessionServiceUrl + `api/session/filtereddevicelist`;
		return this.uvExplorerClient.post<DisplayDeviceListDto>(url, body);
	}

	public getDeviceGroupMembers(sessionGuid: string, netViewGuid: string, agentGuid: string,
		schedDiscGuid: string, schedDiscRsltGuid: string,
		deviceGuids: List<string>): Observable<GetDeviceGroupMembersResultDto> {

		let body = {
			get_device_group_members_request: {
				session_guid: sessionGuid,
				net_view_guid: netViewGuid,
				agent_guid: agentGuid,
				sched_disc_guid: schedDiscGuid,
				sched_disc_result_guid: schedDiscRsltGuid,
				device_guids: [],
			}
		};
		ObjSerUtils.WriteStringValueCollection(body.get_device_group_members_request.device_guids, deviceGuids, {});

		let url = this.appConfig.sessionServiceUrl + `api/session/devicegroupmembers`;
		return this.uvExplorerClient.postObjSer<GetDeviceGroupMembersResultDto>(url, body, GetDeviceGroupMembersResultDto);
	}

	public getDevice(sessionGuid: string, netViewGuid: string, agentGuid: string,
		schedDiscGuid: string, schedDiscRsltGuid: string, source: string, deviceGuids: List<string>)
			: Observable<GetDeviceResultDto> {

		let body = {
			get_device_request: {
				session_guid: sessionGuid,
				net_view_guid: netViewGuid,
				agent_guid: agentGuid,
				sched_disc_guid: schedDiscGuid,
				sched_disc_result_guid: schedDiscRsltGuid,
				source: source,
				device_guids: [],
			}
		};
		ObjSerUtils.WriteStringValueCollection(body.get_device_request.device_guids, deviceGuids, {});

		let url = this.appConfig.sessionServiceUrl + `api/session/device`;
		return this.uvExplorerClient.postObjSer<GetDeviceResultDto>(url, body, GetDeviceResultDto);
	}

	public getDeviceView(sessionGuid: string, netViewGuid: string, agentGuid: string,
		schedDiscGuid: string, schedDiscRsltGuid: string, deviceGuid: string): Observable<DeviceDto> {

		let body = {
			get_device_view_request: {
				session_guid: sessionGuid,
				net_view_guid: netViewGuid,
				agent_guid: agentGuid,
				sched_disc_guid: schedDiscGuid,
				sched_disc_result_guid: schedDiscRsltGuid,
				device_guid: deviceGuid,
			}
		};

		let url = this.appConfig.sessionServiceUrl + `api/session/deviceview`;
		return this.uvExplorerClient.post<DeviceDto>(url, body);
	}

	public getDeviceTopoMap(sessionGuid: string, netViewGuid: string, agentGuid: string,
		schedDiscGuid: string, schedDiscRsltGuid: string, deviceGuid: string): Observable<TopoMap> {

		let body = {
			get_device_topo_map_request: {
				session_guid: sessionGuid,
				net_view_guid: netViewGuid,
				agent_guid: agentGuid,
				sched_disc_guid: schedDiscGuid,
				sched_disc_result_guid: schedDiscRsltGuid,
				device_guid: deviceGuid,
			}
		};

		let url = this.appConfig.sessionServiceUrl + `api/session/devicetopomap`;
		return this.uvExplorerClient.post<TopoMap>(url, body);
	}

	public getReportView(sessionGuid: string, netViewGuid: string, agentGuid: string,
		schedDiscGuid: string, schedDiscRsltGuid: string, reportName: string): Observable<InfoSetView> {

		let body = {
			get_report_view_request: {
				session_guid: sessionGuid,
				net_view_guid: netViewGuid,
				agent_guid: agentGuid,
				sched_disc_guid: schedDiscGuid,
				sched_disc_result_guid: schedDiscRsltGuid,
				report_name: reportName,
			}
		};

		let url = this.appConfig.sessionServiceUrl + `api/session/reportview`;
		return this.uvExplorerClient.post<InfoSetView>(url, body);
	}

	public getAdminReportView(sessionGuid: string, reportName: string): Observable<InfoSetView> {

		let body = {
			get_report_view_request: {
				session_guid: sessionGuid,
				net_view_guid: '',
				agent_guid: '',
				sched_disc_guid: '',
				sched_disc_result_guid: '',
				report_name: reportName,
			}
		};

		let url = this.appConfig.sessionServiceUrl + `api/session/admin/reportview`;
		return this.uvExplorerClient.post<InfoSetView>(url, body);
	}


	public getLayer2Trace(sessionGuid: string, netViewGuid: string, agentGuid: string,
		schedDiscGuid: string, schedDiscRsltGuid: string, deviceGuids: List<string>)
		: Observable<InfoSetView> {

		let body = {
			get_device_request: {
				session_guid: sessionGuid,
				net_view_guid: netViewGuid,
				agent_guid: agentGuid,
				sched_disc_guid: schedDiscGuid,
				sched_disc_result_guid: schedDiscRsltGuid,
				source: "",
				device_guids: [],
			}
		};
		ObjSerUtils.WriteStringValueCollection(body.get_device_request.device_guids, deviceGuids, {});

		let url = this.appConfig.sessionServiceUrl + `api/session/layer2trace`;
		return this.uvExplorerClient.post<InfoSetView>(url, body);
	}


	public getDeviceGroup(sessionGuid: string, netViewGuid: string, vdgGuid: string): Observable<ViewerDeviceGroupDto> {

		let body = {
			get_device_group_request: {
				session_guid: sessionGuid,
				net_view_guid: netViewGuid,
				vdg_guid: vdgGuid,
			}
		};

		let url = this.appConfig.sessionServiceUrl + `api/session/devicegroups/get`;
		return this.uvExplorerClient.postObjSer<ViewerDeviceGroupDto>(url, body, ViewerDeviceGroupDto);
	}

	public createDeviceGroup(sessionGuid: string, vdg: ViewerDeviceGroupDto, parentGuid: string,
		position: number): Observable<DisplayDeviceGroupDto> {

		let body = {
			create_vdg_request: {
				session_guid: sessionGuid,
				parent_guid: parentGuid,
				position: ValueEncoder.EncodeInteger(position)
			}
		};

		vdg.Write(body.create_vdg_request, {});

		let url = this.appConfig.sessionServiceUrl + `api/session/devicegroups/create`;
		let subject = new Subject<DisplayDeviceGroupDto>();

		this.uvExplorerClient.postObjSer<DeviceGroupUpdateResultDto>(url, body, DeviceGroupUpdateResultDto).subscribe(
			(result: DeviceGroupUpdateResultDto) => {
				vdg.CopyProps(result.UpdateResult, true);
				subject.next(result.Ddg);
			},
			(error?) => {
				console.error(error);
				subject.error(error);
			}
		);

		return subject.asObservable();
	}

	public updateDeviceGroup(sessionGuid: string, vdg: ViewerDeviceGroupDto): Observable<DisplayDeviceGroupDto> {
		let body = {
			update_vdg_request: {
				session_guid: sessionGuid
			}
		};
		vdg.Write(body.update_vdg_request, {});

		let url = this.appConfig.sessionServiceUrl + `api/session/devicegroups/update`;
		let subject = new Subject<DisplayDeviceGroupDto>();

		this.uvExplorerClient.putObjSer<DeviceGroupUpdateResultDto>(url, body, DeviceGroupUpdateResultDto).subscribe(
			(result: DeviceGroupUpdateResultDto) => {
				vdg.CopyProps(result.UpdateResult, false);
				subject.next(result.Ddg);
			},
			(error?) => {
				console.error(error);
				subject.error(error);
			});

		return subject.asObservable();
	}

	public deleteDeviceGroupByGuid(sessionGuid: string, netViewGuid: string,
		guid: string, parentGuid: string): Observable<boolean> {
		let body = {
			delete_vdg_by_guid_request: {
				session_guid: sessionGuid,
				net_view_guid: netViewGuid,
				vdg_guid: guid,
				parent_guid: parentGuid
			}
		};

		let url = this.appConfig.sessionServiceUrl + `api/session/devicegroups/delete`;
		return this.uvExplorerClient.deleteNoResult(url, body);
	}

	public deleteMasterDeviceByGuid(sessionGuid: string, netViewGuid: string,
		deviceGuid: string): Observable<boolean> {
		let body = {
			delete_device_by_guid_request: {
				session_guid: sessionGuid,
				net_view_guid: netViewGuid,
				device_guid: deviceGuid,
			}
		};

		let url = this.appConfig.sessionServiceUrl + `api/session/device/delete`;
		return this.uvExplorerClient.deleteNoResult(url, body);
	}


	public runPrtgExport(sessionGuid: string, netViewGuid: string, agentGuid: string,
		schedDiscGuid: string): Observable<boolean> {
		let body = {
			run_prtg_export_request: {
				session_guid: sessionGuid,
				net_view_guid: netViewGuid,
				agent_guid: agentGuid,
				sched_disc_guid: schedDiscGuid,
			}
		};

		let url = this.appConfig.sessionServiceUrl + `api/session/runprtgexport`;
		return this.uvExplorerClient.postNoResult(url, body);
	}

	// Lists the PRTG agents configured on the given netview. Drives the PRTG
	// agent dropdown in the Export-to-PRTG dialogs.
	//
	// The server's ObjSerWriter wraps each item in a `{ prtg_settings_summary:
	// {...} }` envelope. Unwrap here so consumers see clean flat DTOs.
	public listPrtgSettings(netViewGuid: string): Observable<PrtgSettingsSummaryDto[]> {
		let url = this.appConfig.sessionServiceUrl + `api/session/prtgsettings/${netViewGuid}`;
		return this.uvExplorerClient.get<{ prtg_settings_summary: PrtgSettingsSummaryDto }[]>(url).pipe(
			map(arr => (arr || []).map(w => w.prtg_settings_summary))
		);
	}

	// Fetches the PRTG group hierarchy from the selected PRTG agent. The
	// server wraps each item in a `{ prtg_group_node: {...} }` envelope just
	// like listPrtgSettings; unwrap here. Children come back already wrapped
	// in their own envelopes, so a recursive unwrap is needed.
	public listPrtgGroups(netViewGuid: string, prtgSettingsGuid: string): Observable<PrtgGroupNodeDto[]> {
		let url = this.appConfig.sessionServiceUrl +
			`api/session/prtg/groups/${netViewGuid}/${prtgSettingsGuid}`;
		return this.uvExplorerClient.get<{ prtg_group_node: any }[]>(url).pipe(
			map(arr => (arr || []).map(w => DiscResultsWebService.unwrapPrtgGroupNode(w.prtg_group_node)))
		);
	}

	// Fetches the flat list of PRTG maps from the selected PRTG agent. Server
	// wraps each item in a `{ prtg_map: {...} }` envelope; unwrap here. Scalars
	// arrive as strings (ObjSer wire format) so decode id back to a number.
	public listPrtgMaps(netViewGuid: string, prtgSettingsGuid: string): Observable<PrtgMapDto[]> {
		let url = this.appConfig.sessionServiceUrl +
			`api/session/prtg/maps/${netViewGuid}/${prtgSettingsGuid}`;
		return this.uvExplorerClient.get<{ prtg_map: any }[]>(url).pipe(
			map(arr => (arr || []).map(w => {
				const raw = w.prtg_map || w;
				return {
					id: raw && raw.id != null ? ValueEncoder.DecodeInteger(String(raw.id)) : 0,
					name: raw && typeof raw.name === "string" ? raw.name : "",
					tags: raw && typeof raw.tags === "string" ? raw.tags : "",
				} as PrtgMapDto;
			}))
		);
	}

	private static unwrapPrtgGroupNode(raw: any): PrtgGroupNodeDto {
		const children: any[] = raw && raw.child_groups ? raw.child_groups : [];
		// The server's ObjSer wire format encodes scalars as strings, so id
		// arrives as e.g. "42", not 42. Decode it back to a number.
		return {
			id: raw && raw.id != null ? ValueEncoder.DecodeInteger(String(raw.id)) : 0,
			name: raw && typeof raw.name === "string" ? raw.name : "",
			child_groups: children.map(w =>
				DiscResultsWebService.unwrapPrtgGroupNode(w.prtg_group_node || w)),
		};
	}

	// Runs the "Export to PRTG (Data Only)" flow on the server. Sends device,
	// sensor, and tag information to the UVexplorer PRTG service running on
	// the PRTG server. Rejected by the server for hosted-PRTG agents.
	public exportToPrtgDataOnly(sessionGuid: string, netViewGuid: string, agentGuid: string,
		schedDiscGuid: string, schedDiscRsltGuid: string, vdgGuid: string,
		prtgSettingsGuid: string): Observable<boolean> {

		let body = {
			export_to_prtg_data_only_request: {
				session_guid: sessionGuid,
				net_view_guid: netViewGuid,
				agent_guid: agentGuid,
				sched_disc_guid: schedDiscGuid,
				sched_disc_result_guid: schedDiscRsltGuid,
				vdg_guid: vdgGuid,
				prtg_settings_guid: prtgSettingsGuid,
			}
		};

		let url = this.appConfig.sessionServiceUrl + `api/session/topomap/prtg/exportdata`;
		return this.uvExplorerClient.postNoResult(url, body);
	}

	// Runs the full "Export to PRTG" flow on the server. Creates a new PRTG
	// group, adds devices and sensors, and builds a PRTG topology map. The
	// exportName becomes the group/map name in PRTG.
	public exportToPrtg(sessionGuid: string, netViewGuid: string, agentGuid: string,
		schedDiscGuid: string, schedDiscRsltGuid: string, vdgGuid: string,
		prtgSettingsGuid: string, exportName: string,
		options: ExportToPrtgOptions): Observable<boolean> {

		// The server-side ObjSer wire format encodes every scalar property
		// value as a string (booleans as "true"/"false", numbers as decimal
		// strings, etc.) — use ValueEncoder to match.
		let body = {
			export_to_prtg_request: {
				session_guid: sessionGuid,
				net_view_guid: netViewGuid,
				agent_guid: agentGuid,
				sched_disc_guid: schedDiscGuid,
				sched_disc_result_guid: schedDiscRsltGuid,
				vdg_guid: vdgGuid,
				prtg_settings_guid: prtgSettingsGuid,
				export_name: exportName,
				parent_group_id: ValueEncoder.EncodeInteger(options.parentGroupId),

				create_new_group: ValueEncoder.EncodeBoolean(options.createNewGroup),
				create_dependency: ValueEncoder.EncodeBoolean(options.createDependency),
				remove_missing_devices: ValueEncoder.EncodeBoolean(options.removeMissingDevices),
				export_prtg_devices_only: ValueEncoder.EncodeBoolean(options.exportPrtgDevicesOnly),
				export_map_only: ValueEncoder.EncodeBoolean(options.exportMapOnly),
				merge_map_on_export: ValueEncoder.EncodeBoolean(options.mergeMapOnExport),
				export_master_only: ValueEncoder.EncodeBoolean(options.exportMasterNetworkOnly),
				use_template_tags: ValueEncoder.EncodeBoolean(options.useTemplateTags),

				create_topology_map: ValueEncoder.EncodeBoolean(options.createTopologyMap),
				overwrite_existing_topology_map: ValueEncoder.EncodeBoolean(options.overwriteExistingTopologyMap),

				add_ping_sensors: ValueEncoder.EncodeBoolean(options.addPingSensors),
				add_snmp_uptime_sensors: ValueEncoder.EncodeBoolean(options.addSnmpUpTimeSensors),
				add_snmp_cpu_sensors: ValueEncoder.EncodeBoolean(options.addSnmpCpuSensors),
				add_snmp_memory_sensors: ValueEncoder.EncodeBoolean(options.addSnmpMemorySensors),
				add_snmp_disk_free_sensors: ValueEncoder.EncodeBoolean(options.addSnmpDiskFreeSensors),
				add_snmp_traffic_sensors: ValueEncoder.EncodeBoolean(options.addSnmpTrafficSensors),
				use_snmp_if_name: ValueEncoder.EncodeBoolean(options.useSnmpIfName),
				config_all_interfaces_if_up: ValueEncoder.EncodeBoolean(options.configAllInterfacesIfUp),
				config_all_physical_interfaces: ValueEncoder.EncodeBoolean(options.configAllPhysicalInterfaces),
				config_all_uplink_interfaces: ValueEncoder.EncodeBoolean(options.configAllUpLinkInterfaces),
				snmp_if_sensor_format: options.snmpIfSensorFormat,
				config_all_ap_sensors: ValueEncoder.EncodeBoolean(options.configAllApSensors),

				add_wmi_uptime_sensors: ValueEncoder.EncodeBoolean(options.addWmiUpTimeSensors),
				add_wmi_cpu_sensors: ValueEncoder.EncodeBoolean(options.addWmiCpuSensors),
				add_wmi_disk_space_sensors: ValueEncoder.EncodeBoolean(options.addWmiDiskSpaceSensors),
				add_wmi_memory_sensors: ValueEncoder.EncodeBoolean(options.addWmiMemorySensors),
			}
		};

		let url = this.appConfig.sessionServiceUrl + `api/session/topomap/prtg/export`;
		return this.uvExplorerClient.postNoResult(url, body);
	}

	// Runs the "Export to PRTG Map" (map-only) flow on the server. Builds a
	// PRTG topology map onto an existing PRTG group whose devices have already
	// been populated. prtgTargetGroupId can be empty to use the server default.
	public exportToPrtgMap(sessionGuid: string, netViewGuid: string, agentGuid: string,
		schedDiscGuid: string, schedDiscRsltGuid: string, vdgGuid: string,
		prtgSettingsGuid: string, mapName: string,
		prtgTargetGroupId: string,
		options: ExportToPrtgMapOptions): Observable<boolean> {

		// Scalars must be string-encoded to match the ObjSer wire format.
		let body = {
			export_to_prtg_map_request: {
				session_guid: sessionGuid,
				net_view_guid: netViewGuid,
				agent_guid: agentGuid,
				sched_disc_guid: schedDiscGuid,
				sched_disc_result_guid: schedDiscRsltGuid,
				vdg_guid: vdgGuid,
				prtg_settings_guid: prtgSettingsGuid,
				prtg_target_group_id: prtgTargetGroupId,
				map_name: mapName,

				overwrite_existing_topology_map: ValueEncoder.EncodeBoolean(options.overwriteExistingTopologyMap),
				show_if_labels: ValueEncoder.EncodeBoolean(options.showIfLabels),
				default_icon_height: ValueEncoder.EncodeInteger(options.defaultIconHeight),
				default_icon_width: ValueEncoder.EncodeInteger(options.defaultIconWidth),
				add_ping_sensors: ValueEncoder.EncodeBoolean(options.addPingSensors),
			}
		};

		let url = this.appConfig.sessionServiceUrl + `api/session/topomap/prtg/exportmap`;
		return this.uvExplorerClient.postNoResult(url, body);
	}

	public runAssetPandaExport(sessionGuid: string, netViewGuid: string, agentGuid: string,
		schedDiscGuid: string): Observable<boolean> {
		let body = {
			run_assetpanda_export_request: {
				session_guid: sessionGuid,
				net_view_guid: netViewGuid,
				agent_guid: agentGuid,
				sched_disc_guid: schedDiscGuid,
			}
		};

		let url = this.appConfig.sessionServiceUrl + `api/session/runassetpandaexport`;
		return this.uvExplorerClient.postNoResult(url, body);
	}

	public runHaloExport(sessionGuid: string, netViewGuid: string, agentGuid: string,
		schedDiscGuid: string): Observable<boolean> {
		let body = {
			run_hudu_export_request: {
				session_guid: sessionGuid,
				net_view_guid: netViewGuid,
				agent_guid: agentGuid,
				sched_disc_guid: schedDiscGuid,
			}
		};

		let url = this.appConfig.sessionServiceUrl + `api/session/runhaloexport`;
		return this.uvExplorerClient.postNoResult(url, body);
	}

	public runHuduExport(sessionGuid: string, netViewGuid: string, agentGuid: string,
		schedDiscGuid: string): Observable<boolean> {
		let body = {
			run_hudu_export_request: {
				session_guid: sessionGuid,
				net_view_guid: netViewGuid,
				agent_guid: agentGuid,
				sched_disc_guid: schedDiscGuid,
			}
		};

		let url = this.appConfig.sessionServiceUrl + `api/session/runhuduexport`;
		return this.uvExplorerClient.postNoResult(url, body);
	}

	public runITGlueExport(sessionGuid: string, netViewGuid: string, agentGuid: string,
		schedDiscGuid: string): Observable<boolean> {
		let body = {
			run_itglue_export_request: {
				session_guid: sessionGuid,
				net_view_guid: netViewGuid,
				agent_guid: agentGuid,
				sched_disc_guid: schedDiscGuid,
			}
		};

		let url = this.appConfig.sessionServiceUrl + `api/session/runitglueexport`;
		return this.uvExplorerClient.postNoResult(url, body);
	}

	public runJiraExport(sessionGuid: string, netViewGuid: string, agentGuid: string,
		schedDiscGuid: string): Observable<boolean> {
		let body = {
			run_jira_export_request: {
				session_guid: sessionGuid,
				net_view_guid: netViewGuid,
				agent_guid: agentGuid,
				sched_disc_guid: schedDiscGuid,
			}
		};

		let url = this.appConfig.sessionServiceUrl + `api/session/runjiraexport`;
		return this.uvExplorerClient.postNoResult(url, body);
	}

	public rebuildMasterNetwork(sessionGuid: string, netViewGuid: string)
		: Observable<boolean> {
		let body = {
			rebuild_master_network_request: {
				session_guid: sessionGuid,
				net_view_guid: netViewGuid,
			}
		};

		let url = this.appConfig.sessionServiceUrl + `api/session/rebuildmasternetwork`;
		return this.uvExplorerClient.postNoResult(url, body);
	}

	public exportDeviceData(sessionGuid: string, netViewGuid: string, agentGuid: string,
		schedDiscGuid: string, schedDiscRsltGuid: string): Observable<Blob> {

		let body = {
			export_device_data_request: {
				session_guid: sessionGuid,
				net_view_guid: netViewGuid,
				agent_guid: agentGuid,
				sched_disc_guid: schedDiscGuid,
				sched_disc_result_guid: schedDiscRsltGuid,
			}
		};

		let url = this.appConfig.sessionServiceUrl + `api/session/devicedata/export`;
		return this.uvExplorerClient.postWithBinaryResult(url, body);
	}

	public diff(sessionGuid: string, netViewGuid: string, agentGuid: string,
		schedDiscGuid: string, schedDiscRsltGuid: string,
		leftText: string, rightText: string): Observable<DiffResult> {

		let body = {
			diff_request: {
				session_guid: sessionGuid,
				net_view_guid: netViewGuid,
				agent_guid: agentGuid,
				sched_disc_guid: schedDiscGuid,
				sched_disc_result_guid: schedDiscRsltGuid,
				left_text: leftText,
				right_text: rightText,
			}
		};

		let url = this.appConfig.sessionServiceUrl + `api/session/diff`;
		return this.uvExplorerClient.postObjSer<DiffResult>(url, body, DiffResult);
	}

	public getIpMacSightings(sessionGuid: string, netViewGuid: string, agentGuid: string, schedDiscGuid: string,
		schedDiscRsltGuid: string, ipAddress: string, macAddress: string, addressType: AddressType): Observable<InfoSetView> {

		let body = {
			get_ip_or_mac_sightings_request: {
				session_guid: sessionGuid,
				net_view_guid: netViewGuid,
				agent_guid: agentGuid,
				sched_disc_guid: schedDiscGuid,
				sched_disc_result_guid: schedDiscRsltGuid,
				ip_address: ipAddress,
				mac_address: macAddress,
			}
		};

		let url = this.appConfig.sessionServiceUrl + `api/session/${addressType === AddressType.Ip ? "ipsightings" : "macsightings"}`;
		return this.uvExplorerClient.post<InfoSetView>(url, body);
	}

	public searchMasterDevices(searchText: string, searchType: string, skip: number, limit: number): Observable<SearchDeviceListDto> {

		let body = {
			search_master_devices_request: {
				search_text: searchText,
				search_type: searchType,
				skip: ValueEncoder.EncodeInteger(skip),
				limit: ValueEncoder.EncodeInteger(limit),
			}
		};

		let url = this.appConfig.sessionServiceUrl + `api/data/masterdevices`;
		return this.uvExplorerClient.post<SearchDeviceListDto>(url, body);
	}

	public getMasterDeviceView(masterDeviceGuid: string): Observable<DeviceDto> {

		let url = this.appConfig.sessionServiceUrl + `api/session/masterdeviceview/${masterDeviceGuid}`;
		return this.uvExplorerClient.get<DeviceDto>(url);
	}

	public getMasterDeviceTopoMap(masterDeviceGuid: string): Observable<TopoMap> {

		let url = this.appConfig.sessionServiceUrl + `api/session/masterdevicetopomap/${masterDeviceGuid}`;
		return this.uvExplorerClient.get<TopoMap>(url);
	}

	public deleteMasterDeviceConfig(deviceGuid: string, captureKey: string, lastTimeStamp: string): Observable<boolean> {
		let url = this.appConfig.dataServiceUrl + `api/data/masterdevices/config`;

		let body = {
			delete_config_entry_request: {
				device_guid: deviceGuid,
				capture_key: captureKey,
				last_capture_time: lastTimeStamp,
			}
		};

		return this.uvExplorerClient.deleteNoResult(url, body);
	}

}
